import { AREAS, type Area } from "@69ly/shared";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Segmented } from "./Segmented.tsx";

/**
 * The most common area in a list, sites first: an execution with three throws
 * on A and one in mid goes to A. Null for an empty list.
 */
export function mostCommonArea(areas: Area[]): Area | null {
  if (areas.length === 0) return null;
  const count = (a: Area) => areas.filter((x) => x === a).length;
  const sites = (["a", "b"] as const).filter((a) => count(a) > 0);
  const pool: readonly Area[] = sites.length ? sites : AREAS;
  return [...pool].sort((x, y) => count(y) - count(x))[0] ?? null;
}

/**
 * Area picker that follows the map: `auto` comes from where things land, and
 * stays in use until the player picks another area themselves (`choice`).
 */
export function AreaField({
  label,
  auto,
  choice,
  onChoice,
  autoHelp,
  noAutoHelp,
  detail,
}: {
  label: string;
  auto: Area | null;
  /** The player's own pick, or null to follow `auto`. */
  choice: Area | null;
  onChoice: (a: Area | null) => void;
  autoHelp: string;
  noAutoHelp: string;
  /** Where things are thrown from and land, shown with the area. */
  detail?: ReactNode;
}) {
  const { t } = useTranslation();
  const value = choice ?? auto ?? "other";
  return (
    <div className="area-field">
      <Segmented
        label={label}
        options={AREAS}
        value={value}
        onChange={(a) => onChoice(a === auto ? null : a)}
        render={(a) => (
          <>
            {t(`areaShort.${a}`)}
            {a === auto && choice === null && (
              <>
                {" "}
                <span className="auto-tag">{t("areaField.auto")}</span>
              </>
            )}
          </>
        )}
      />
      {detail}
      <p className="muted small">
        {choice !== null && auto ? (
          <>
            {t("areaField.manual")}{" "}
            <button type="button" className="link-button" onClick={() => onChoice(null)}>
              {t("areaField.backToAuto", { area: t(`area.${auto}`) })}
            </button>
          </>
        ) : auto ? (
          autoHelp
        ) : (
          noAutoHelp
        )}
      </p>
    </div>
  );
}

/** "Thrown from T Spawn → lands on Jungle", the callouts in bold. */
export function PlacesSummary({ from, to }: { from: string | null; to: string | null }) {
  const { t } = useTranslation();
  if (!from && !to) return null;
  return (
    <p className="places-summary">
      {from && (
        <span>
          {t("places.thrownFrom")} <b>{from}</b>
        </span>
      )}
      {from && to && <span aria-hidden="true"> → </span>}
      {to && (
        <span>
          {t(from ? "places.landsOn" : "places.landsOnAlone")} <b>{to}</b>
        </span>
      )}
    </p>
  );
}

/** The distinct callouts a list of throws lands on, most hit first: "Jungle ×2 · CT Spawn". */
export function LandingList({ places }: { places: (string | null)[] }) {
  const { t } = useTranslation();
  const counts = new Map<string, number>();
  for (const p of places) if (p) counts.set(p, (counts.get(p) ?? 0) + 1);
  if (counts.size === 0) return null;
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return (
    <div className="landing-list">
      <span className="muted small">{t("places.landsOnList", { count: counts.size })}</span>
      <ul>
        {sorted.map(([name, n]) => (
          <li key={name} className="place-chip">
            {name}
            {n > 1 && <span className="muted"> ×{n}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
